import React, { useEffect, useState } from 'react';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import useAuth from '../hooks/useAuth';
import { toast } from 'sonner';
import {
  searchAddressByPostcode,
  formatAddressForDisplay,
} from '../api/getAddress'

export default function ClientAddressForm({ clientAddressData, setClientAddressData }) {
  const [editing, setEditing] = useState(false);
  const { auth } = useAuth();

  const [isSearching, setIsSearching] = useState(false)
  const [addresses, setAddresses] = useState([])
  const [showAddresses, setShowAddresses] = useState(false)
  const [postcodeSearch, setPostcodeSearch] = useState('')
  const [searchError, setSearchError] = useState('')

  useEffect(() => {

    const userAddress = auth?.user?.user_info?.address;

    if (userAddress && Object.keys(userAddress).length > 0) {
      setClientAddressData((prev) => ({
        ...prev,
        addressLine1: userAddress.addressLine1 || '',
        addressLine2: userAddress.addressLine2 || '',
        city: userAddress.city || '',
        postcode: userAddress.postcode || '',
        country: userAddress.country || 'United Kingdom',
        countryCode: userAddress.countryCode || 'GB',
        phoneNumber: userAddress.phoneNumber || '',
      }));

    
    } else {
      setClientAddressData((prev) => ({
        ...prev,
        country: 'United Kingdom',
        countryCode: 'GB',
        phoneNumber: '',
      }));
    }
  }, [auth, setClientAddressData]);

  const handleSaveClick = async () => {
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handlePostcodeSearch = async () => {
    const postcode = postcodeSearch.trim()

    if (!postcode) {
      setSearchError('Please enter a postcode')
      return
    }

    setIsSearching(true)
    setSearchError('')
    setShowAddresses(false)

    try {
      const foundAddresses = await searchAddressByPostcode(postcode)

      setAddresses(foundAddresses)
      setShowAddresses(foundAddresses.length > 0)

      if (foundAddresses.length === 0) {
        setSearchError('No addresses found for this postcode')
      }
    } catch (error) {
      setSearchError(error.message)
      setAddresses([])
      setShowAddresses(false)
    } finally {
      setIsSearching(false)
    }
  }

  const handleAddressSelect = (selectedAddress) => {
    
    setClientAddressData((prev) => ({
      ...prev,
      addressLine1: selectedAddress.line_1,
      addressLine2: selectedAddress.line_2 || '',
      city: selectedAddress.town_or_city,
      county: selectedAddress.county,
      country: selectedAddress.country,
      postcode: selectedAddress.postcode,
    }));
    setShowAddresses(false)
    setAddresses([])
  }


  return (
    <div className="mx-auto mt-10">
      {!editing ? (
        <div className="bg-white shadow-md rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p>{clientAddressData.addressLine1 || 'No address entered'}</p>
              {clientAddressData.addressLine2 && <p>{clientAddressData.addressLine2}</p>}
              <p>
                {clientAddressData.city} {clientAddressData.postcode}
              </p>
              <p>{clientAddressData.country}</p>
              {clientAddressData.phoneNumber && <p>Phone: {clientAddressData.phoneNumber}</p>}
            </div>
            <button
              onClick={() => setEditing(true)}
              className="text-blue-600 hover:underline text-sm font-medium"
            >
              Edit
            </button>
          </div>
        </div>
      ) : (
        <div className="sm:col-span-6">
          <label htmlFor="postcode" className="block text-sm/6 font-medium">
            Search via Postcode
          </label>
          <div className="flex items-end gap-2">
            <input
              type="text"
              placeholder="Postcode"
              onChange={(e) => setPostcodeSearch(e.target.value)}
              className="mt-2 block w-1/2 rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />
            <div className="w-1/2 flex items-center space-x-2">
              <button
                type="button"
                onClick={handlePostcodeSearch}
                disabled={isSearching}
                className="text-sm inline-flex justify-center items-center cursor-pointer bg-teal-500 border-teal-500 hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white font-bold py-2 px-4 border rounded-lg"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Error message */}
          {searchError && (
            <div className="mt-2 text-sm text-red-600">{searchError}</div>
          )}

          {/* Address dropdown */}
          {showAddresses && addresses.length > 0 && (
            <div className="mt-2 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select an address:
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
                {addresses.map((address) => (
                  <button
                    key={address.id}
                    type="button"
                    onClick={() => handleAddressSelect(address)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                  >
                    <div className="text-sm text-gray-900">
                      {formatAddressForDisplay(address)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <label htmlFor="address" className="block text-sm/6 font-medium mt-6">
            Address
          </label>
          <input
            type="text"
            placeholder="Address Line 1"
            value={clientAddressData?.addressLine1 || ''}
            onChange={(e) =>
              setClientAddressData((prev) => ({
                ...prev,
                  addressLine1: e.target.value,
              }))
            }
            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />
          <input
            type="text"
            placeholder="Address Line 2"
            value={clientAddressData?.addressLine2 || ''}
            onChange={(e) =>
              setClientAddressData((prev) => ({
                ...prev,
                  addressLine2: e.target.value,
              }))
            }
            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />
          <div className="grid grid-cols-8 gap-2">
            <input
              type="text"
              placeholder="Town/City"
              value={clientAddressData?.city || ''}
              onChange={(e) =>
                setClientAddressData((prev) => ({
                  ...prev,
                    city: e.target.value,
                }))
              }
              className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base col-span-2 border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />

            <input
              type="text"
              placeholder="Postcode"
              value={clientAddressData?.postcode || ''}
              onChange={(e) =>
                setClientAddressData((prev) => ({
                  ...prev,
                    postcode: e.target.value,
                }))
              }
              className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base col-span-2 border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />

            <input
              type="text"
              placeholder="Country"
              value={clientAddressData?.country || ''}
              onChange={(e) =>
                setClientAddressData((prev) => ({
                  ...prev,
                    country: e.target.value,
                }))
              }
              className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border col-span-4 border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <button
              type="button"
              onClick={handleSaveClick}
              className="w-24 h-12 px-6 py-3 bg-[#7B5FA6] rounded-md text-sm font-bold hover:bg-[#DDCCDF] text-white flex justify-center items-center"
            >
              Save
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="min-h-[48px] px-4 py-2 bg-white text-black rounded hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>

        </div>


      )}
    </div>
  );
}
